import { Link } from "react-router-dom";
import { useState } from "react";
import { Heart } from "lucide-react";
import { useStore } from "@/lib/store";
import { getImageUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";

export default function ProductCard({ product }) {
  const { formatPrice, t, toggleFavorite, isFavorite } = useStore();
  const [imgError, setImgError] = useState(false);
  const hasDiscount = product.old_price && product.old_price > product.price;
  const img = product.images?.[0];
  const statusLabels = { "pre-order": "ПОД ЗАКАЗ", "sold-out": "РАСПРОДАНО" };
  const statusLabel = statusLabels[product.status];
  const fav = isFavorite(product.id);

  const handleFavoriteClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggleFavorite(product);
  };

  return (
    <Link to={`/product/${product.id}`}
      className="group relative bg-black border-r border-b border-zinc-800 overflow-hidden block"
      data-testid={`product-card-${product.id}`}>
      {/* Image container — fixed 4:5 aspect, image centered and cropped */}
      <div className="aspect-[4/5] bg-zinc-950 overflow-hidden relative">
        {img && !imgError ? (
          <img src={getImageUrl(img, 600)} alt={product.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
            onError={() => setImgError(true)} />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-zinc-700 text-xs font-mono">
            {t ? t("noImage") : "NO IMAGE"}
          </div>
        )}
        {/* Status badge (ПОД ЗАКАЗ / РАСПРОДАНО) — top left (replaces condition since pre-order = obviously new) */}
        {statusLabel && (
          <Badge variant="outline" className="absolute top-2 left-2 z-10 bg-black/70 text-white border-zinc-600 text-[10px] font-mono rounded-none backdrop-blur-sm">
            {statusLabel}
          </Badge>
        )}
        {/* Favorite button — always top right */}
        <button onClick={handleFavoriteClick}
          className="absolute top-2 right-2 z-10 w-8 h-8 flex items-center justify-center bg-black/50 backdrop-blur-sm border border-zinc-600/50 hover:bg-black/80 transition-colors"
          data-testid={`fav-btn-${product.id}`}>
          <Heart className={`w-4 h-4 transition-colors ${fav ? "fill-white text-white" : "text-white"}`} strokeWidth={1.5} />
        </button>
      </div>
      {/* Condition badge — only if no status label (pre-order replaces it) */}
      {product.condition && !statusLabel && (
        <Badge variant="outline" className="absolute top-2 left-2 bg-black/70 text-white border-zinc-600 text-[10px] font-mono rounded-none backdrop-blur-sm">
          {product.condition}
        </Badge>
      )}
      <div className="p-2 md:p-3">
        <p className="text-[11px] md:text-xs font-display tracking-wide truncate text-zinc-200">{product.title}</p>
        <div className="flex items-center gap-2 mt-1">
          {hasDiscount && (
            <span className="text-[10px] font-mono text-zinc-500 line-through-red">{formatPrice(product.old_price)}</span>
          )}
          <span className="text-xs font-mono font-bold text-white">{formatPrice(product.price)}</span>
        </div>
      </div>
    </Link>
  );
}